// IR-1226 P1-D: edit per-platform default group-capacity limits.
// Per-avatar overrides live on the avatar edit form; blank override → these.
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useCapacityDefaults, useUpdateCapacityDefault } from "@/hooks/use-fleet";
import { useEffect, useState } from "react";
import { parseCapacityLimit } from "./avatars-tab.helpers";

const PLATFORMS = ["telegram", "whatsapp", "signal"];

export function CapacityDefaultsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: defaults, isLoading } = useCapacityDefaults();
  const update = useUpdateCapacityDefault();
  const [form, setForm] = useState<Record<string, string>>({});

  // Seed inputs once the GET lands (and when reopened after a save elsewhere).
  useEffect(() => {
    if (!defaults) return;
    setForm(
      Object.fromEntries(
        PLATFORMS.map((p) => [
          p,
          String(defaults.find((d) => d.platform === p)?.default_limit ?? ""),
        ]),
      ),
    );
  }, [defaults]);

  function save(platform: string) {
    const limit = parseCapacityLimit(form[platform] ?? "");
    if (limit === null) return;
    update.mutate({ platform, default_limit: limit });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-[380px]">
        <DialogTitle>Capacity defaults</DialogTitle>
        <DialogDescription>
          Default max groups per avatar, by platform. A per-avatar limit on the avatar form
          overrides these.
        </DialogDescription>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 4 }}>
          {isLoading && <span className="iris-meta">Loading…</span>}
          {!isLoading &&
            PLATFORMS.map((p) => (
              <div key={p} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="iris-meta" style={{ width: 90 }}>
                  {p}
                </span>
                <input
                  value={form[p] ?? ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, [p]: e.target.value }))}
                  placeholder="no default"
                  className="iris-input"
                  inputMode="numeric"
                  style={{ width: 110 }}
                />
                <button
                  type="button"
                  className="iris-btn"
                  disabled={update.isPending || parseCapacityLimit(form[p] ?? "") === null}
                  onClick={() => save(p)}
                >
                  Save
                </button>
              </div>
            ))}
          {update.isError && (
            <span style={{ fontSize: 11, color: "var(--iris-danger, #e5484d)" }}>
              Save failed — check the value and try again.
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
